import { computed, ComputedRef, Ref, isRef, unref } from 'vue'
import { get } from 'lodash'
import { DynamicFormField } from '../../DynamicFormTypes'
import { useSelectFormatting } from './useSelectFormatting'
import { useNumberFormatting } from './useNumberFormatting'
import { useDateFormatting } from './useDateFormatting'
import { useBooleanFormatting } from './useBooleanFormatting'
import { useFileFormatting } from './useFileFormatting'

interface UseFieldFormattingProps {
  model: Record<string, any> | Ref<Record<string, any>> | ComputedRef<Record<string, any>>
  field: DynamicFormField | Ref<DynamicFormField> | ComputedRef<DynamicFormField>
}

export function useFieldFormatting({ model, field }: UseFieldFormattingProps): ComputedRef<any> {
  const selectFormatting = useSelectFormatting()
  const numberFormatting = useNumberFormatting()
  const dateFormatting = useDateFormatting()
  const booleanFormatting = useBooleanFormatting()
  const fileFormatting = useFileFormatting()

  return computed(() => {
    const modelValue = unref(model)
    const fieldValue = unref(field)

    if (!modelValue || !fieldValue) return ''

    // Custom value getter has priority
    if (fieldValue.valueGetter) {
      return fieldValue.valueGetter(modelValue, fieldValue)
    }

    // User system type
    if (fieldValue.systemType === 'user' || fieldValue.systemType === 'users') {
      if (fieldValue.userModel) {
        return get(modelValue, fieldValue.userModel)
      }
      return modelValue
    }

    // File/Photo system type
    if (fieldValue.systemType === 'Photo' || fieldValue.systemType === 'File') {
      if (!fieldValue.model) return ''
      return fileFormatting.formatFileValue(get(modelValue, fieldValue.model))
    }

    // Select fields with items
    if ('items' in fieldValue && fieldValue.items) {
      return selectFormatting.formatSelectValue(modelValue, fieldValue)
    }

    // Select fields with dataSource
    if ('dataSource' in fieldValue && fieldValue.dataSource) {
      return selectFormatting.formatDataSourceValue(modelValue, fieldValue)
    }

    // Boolean format
    if ('format' in fieldValue && fieldValue.format === 'boolean') {
      if (!fieldValue.model) return ''
      return booleanFormatting.formatBooleanValue(get(modelValue, fieldValue.model))
    }

    // Number format
    if (('format' in fieldValue && fieldValue.format === 'number') || fieldValue.component === 'dynamic-number-field') {
      if (!fieldValue.model) return ''
      const value = get(modelValue, fieldValue.model)
      return numberFormatting.formatNumberValue(value, fieldValue)
    }

    // Date format
    if ('format' in fieldValue && fieldValue.format === 'date') {
      if (!fieldValue.model) return ''
      const value = get(modelValue, fieldValue.model)
      return dateFormatting.formatDateValue(value, fieldValue)
    }

    // Default: return raw value
    if (!fieldValue.model) return ''
    return get(modelValue, fieldValue.model)
  })
}
